import { traduzirErroSupabase } from '@octawash/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, ShieldAlert, UserCog } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';

export function EditarDadosPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const cli = useQuery({
    queryKey: ['cliente-edit', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_clientes')
        .select('nom_cliente, num_cpf_cnpj, des_email, num_telefone, num_whatsapp, dta_nascimento')
        .eq('cod_auth_user', user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const [form, setForm] = useState({
    nom_cliente: '',
    num_telefone: '',
    num_whatsapp: '',
    des_email: '',
  });
  const [motivoNomeCpf, setMotivoNomeCpf] = useState('');
  const [editandoNome, setEditandoNome] = useState(false);

  useEffect(() => {
    if (cli.data) {
      setForm({
        nom_cliente: cli.data.nom_cliente ?? '',
        num_telefone: cli.data.num_telefone ?? '',
        num_whatsapp: cli.data.num_whatsapp ?? '',
        des_email: cli.data.des_email ?? '',
      });
    }
  }, [cli.data]);

  const salvar = useMutation({
    mutationFn: async () => {
      const campos: Record<string, string> = {};
      if (cli.data?.num_telefone !== form.num_telefone) campos.num_telefone = form.num_telefone;
      if (cli.data?.num_whatsapp !== form.num_whatsapp) campos.num_whatsapp = form.num_whatsapp;
      if (cli.data?.des_email !== form.des_email) campos.des_email = form.des_email;
      if (editandoNome && cli.data?.nom_cliente !== form.nom_cliente) {
        campos.nom_cliente = form.nom_cliente;
      }

      if (Object.keys(campos).length === 0) {
        throw new Error('Nada a alterar');
      }

      const { data, error } = await supabase.functions.invoke('corrigir-dados-titular', {
        body: { campos, motivo: motivoNomeCpf || undefined },
      });
      if (error) throw error;
      return data as {
        campos_aplicados: string[];
        campos_aguardando_dpo: string[];
        mensagem: string;
      };
    },
    onSuccess: (r) => {
      toast.success(r.mensagem);
      navigate('/perfil');
    },
    onError: (e: Error) => toast.error('Falha', { description: traduzirErroSupabase(e) }),
  });

  return (
    <div className="space-y-5 p-4 pb-24">
      <motion.div
        className="octa-page-header"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/perfil')}
          className="text-muted-foreground -ml-2 mb-2 h-8 px-2"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Voltar
        </Button>
        <h1>Editar meus dados</h1>
        <p>Mantenha tudo em dia para receber os avisos certos.</p>
      </motion.div>

      {/* Auto-edição */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08, duration: 0.45 }}
      >
        <Card variant="default">
          <CardContent className="space-y-4 p-5">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-xl">
                <UserCog className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-bold tracking-tight">Auto-edicao</h2>
                <p className="text-muted-foreground text-xs">Aplica direto, sem aprovacao.</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={form.des_email}
                onChange={(e) => setForm({ ...form, des_email: e.target.value })}
                className="h-12 rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tel">Telefone fixo (opcional)</Label>
              <Input
                id="tel"
                value={form.num_telefone}
                onChange={(e) => setForm({ ...form, num_telefone: e.target.value })}
                className="h-12 rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wa">WhatsApp</Label>
              <Input
                id="wa"
                value={form.num_whatsapp}
                onChange={(e) => setForm({ ...form, num_whatsapp: e.target.value })}
                className="h-12 rounded-xl"
              />
              <p className="text-muted-foreground text-xs">
                Trocar WhatsApp invalida sua validação atual; você vai precisar refazer o OTP.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Dados sensíveis */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.16, duration: 0.45 }}
      >
        <Card variant="default" className="border-stone-200/60 dark:border-stone-700/40">
          <CardContent className="space-y-4 p-5">
            <div className="flex items-center gap-3">
              <div className="octa-pastel-soft flex h-10 w-10 items-center justify-center rounded-xl">
                <ShieldAlert className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-bold tracking-tight">Dados sensiveis</h2>
                <p className="text-muted-foreground text-xs">
                  Nome / CPF passam por aprovacao do DPO em ate 15 dias.
                </p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nome">Nome completo</Label>
              <Input
                id="nome"
                value={form.nom_cliente}
                onChange={(e) => {
                  setEditandoNome(true);
                  setForm({ ...form, nom_cliente: e.target.value });
                }}
                className="h-12 rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label>CPF / CNPJ</Label>
              <Input value={cli.data?.num_cpf_cnpj ?? ''} disabled className="h-12 rounded-xl" />
              <p className="text-muted-foreground text-xs">
                Para alterar CPF/CNPJ, fale com o DPO diretamente.
              </p>
            </div>
            {editandoNome && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-1.5"
              >
                <Label htmlFor="motivo">Motivo da alteração do nome (obrigatório)</Label>
                <Input
                  id="motivo"
                  value={motivoNomeCpf}
                  onChange={(e) => setMotivoNomeCpf(e.target.value)}
                  placeholder="Ex: casamento, troca de gênero, erro de cadastro..."
                  required
                  className="h-12 rounded-xl"
                />
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      <div className="flex gap-2">
        <motion.div whileTap={{ scale: 0.97 }} className="flex-1">
          <Button
            size="xl"
            onClick={() => salvar.mutate()}
            disabled={salvar.isPending || (editandoNome && !motivoNomeCpf)}
            className="bg-octa-gradient shadow-octa-glow w-full text-white"
          >
            <Save className="mr-2 h-4 w-4" />
            {salvar.isPending ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </motion.div>
        <Button variant="outline" size="xl" onClick={() => navigate('/perfil')} className="flex-1">
          Cancelar
        </Button>
      </div>
    </div>
  );
}
